<template>
  <div class="duration-picker">
    <div class="time-inputs-row">
      <div class="time-field" v-for="(label, key) in fields" :key="key">
        <label class="input-label">{{ label }}</label>
        <v-text-field
          v-model="values[key]"
          type="number"
          :min="0"
          :max="getMaxValue(key)"
          dense
          outlined
          hide-details
          class="time-input"
          @input="updateDuration(key)"
        />
      </div>
    </div>
    <div v-if="debug" class="debug-info mt-3">
      <div>Current UTC DateTime: {{ currentDateTimeUtc }}</div>
      <div>Result DateTime: {{ resultDateTime }}</div>
      <div>C# DateTime Ticks: {{ dateTimeTicks }}</div>
    </div>
  </div>
</template>
<script>
export default {
  name: 'DurationPicker',
  props: {
    modelValue: {
      type: Object,
      default: () => ({
        years: 0,
        months: 0,
        days: 0,
        hours: 0,
        minutes: 0
      })
    },
    dateTimeUtc: {
      type: String,
      default: null
    },
    debug: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      values: {
        years: 0,
        months: 0,
        days: 0,
        hours: 0,
        minutes: 0
      },
      fields: {
        years: this.$t('Delivery.ClientOrders.years'),
        months: this.$t('Delivery.ClientOrders.months'),
        days: this.$t('Delivery.ClientOrders.days'),
        hours: this.$t('Delivery.ClientOrders.hours'),
        minutes: this.$t('Delivery.ClientOrders.minutes')
      },
      maxValues: {
        years: Number.MAX_SAFE_INTEGER, 
        months: 12,
        days: 31,
        hours: 24,
        minutes: 60
      },
      currentDateTimeUtc: new Date().toISOString()
    };
  },
  computed: {
    formattedDuration() {
      const parts = [];
      if (this.values.years > 0) parts.push(`${this.values.years}y`);
      if (this.values.months > 0) parts.push(`${this.values.months}m`);
      if (this.values.days > 0) parts.push(`${this.values.days}d`);
      if (this.values.hours > 0) parts.push(`${this.values.hours}h`);
      if (this.values.minutes > 0) parts.push(`${this.values.minutes}min`);
      
      return parts.length > 0 ? parts.join(" ") : "0 min";
    },
    resultDateTime() {
      const date = this.dateTimeUtc ? new Date(this.dateTimeUtc) : new Date();
      
      date.setUTCFullYear(date.getUTCFullYear() + parseInt(this.values.years || 0));
      date.setUTCMonth(date.getUTCMonth() + parseInt(this.values.months || 0));
      date.setUTCDate(date.getUTCDate() + parseInt(this.values.days || 0));
      date.setUTCHours(date.getUTCHours() + parseInt(this.values.hours || 0));
      date.setUTCMinutes(date.getUTCMinutes() + parseInt(this.values.minutes || 0));
      
      return date.toISOString();
    },
    dateTimeTicks() {
      const jsTime = new Date(this.resultDateTime).getTime();
      const cSharpTicks = (jsTime + 62135596800000) * 10000;
      return cSharpTicks.toString();
    }
  },
  created() {
    if (this.dateTimeUtc) {
      this.currentDateTimeUtc = this.dateTimeUtc;
    }
    
    if (this.modelValue) {
      const sanitizedValues = this.sanitizeAllValues({ ...this.modelValue });
      this.values = { ...this.values, ...sanitizedValues };
    } else if (this.dateTimeUtc) {
      this.calculateDurationFromDateTime();
    }
    
  },
  watch: {
    modelValue: {
      handler(newVal) {
        if (newVal) {
          const sanitizedValues = this.sanitizeAllValues({ ...newVal });
          this.values = { ...this.values, ...sanitizedValues };
        }
      },
      deep: true
    },
    dateTimeUtc: {
      handler(newVal) {
        if (newVal) {
          this.currentDateTimeUtc = newVal;
          this.calculateDurationFromDateTime();
        }
      }
    }
  },
  methods: {
    getMaxValue(key) {
      return this.maxValues[key];
    },
    
    sanitizeValue(key, value) {
      const numValue = parseInt(value) || 0;
      return Math.min(Math.max(0, numValue), this.maxValues[key]);
    },
    
    sanitizeAllValues(values) {
      const sanitized = {};
      Object.keys(this.fields).forEach(key => {
        if (values[key] !== undefined) {
          sanitized[key] = this.sanitizeValue(key, values[key]);
        }
      });
      return sanitized;
    },
    
    updateDuration(changedKey) {
      this.values[changedKey] = this.sanitizeValue(changedKey, this.values[changedKey]);
      
      const updatedData = {
        ...this.values,
        dateTimeUtc: this.resultDateTime,
        dateTimeTicks: this.dateTimeTicks,
        formattedDuration: this.formattedDuration
      };
      
      
      this.$emit('update:modelValue', updatedData);
      
      this.$emit('durationChange', updatedData);
    },
    
    calculateDurationFromDateTime() {
      const targetDate = new Date(this.dateTimeUtc);
      const now = new Date();
      
      this.values = {
        years: 0,
        months: 0,
        days: 0,
        hours: 0,
        minutes: 0
      };
      
      this.values.years = targetDate.getUTCFullYear() - now.getUTCFullYear();
      this.values.months = targetDate.getUTCMonth() - now.getUTCMonth();
      this.values.days = targetDate.getUTCDate() - now.getUTCDate();
      this.values.hours = targetDate.getUTCHours() - now.getUTCHours();
      this.values.minutes = targetDate.getUTCMinutes() - now.getUTCMinutes();
      
      this.normalizeTimeValues();
      
      const sanitized = this.sanitizeAllValues(this.values);
      this.values = sanitized;
    },
    
    normalizeTimeValues() {
      while (this.values.minutes < 0) {
        this.values.minutes += 60;
        this.values.hours -= 1;
      }
      
      while (this.values.minutes >= 60) {
        this.values.minutes -= 60;
        this.values.hours += 1;
      }
      
      while (this.values.hours < 0) {
        this.values.hours += 24;
        this.values.days -= 1;
      }
      
      while (this.values.hours >= 24) {
        this.values.hours -= 24;
        this.values.days += 1;
      }
      
      while (this.values.days < 0) {
        this.values.days += 30;
        this.values.months -= 1;
      }
      
      while (this.values.months < 0) {
        this.values.months += 12;
        this.values.years -= 1;
      }
      
      while (this.values.months >= 12) {
        this.values.months -= 12;
        this.values.years += 1;
      }
    }
  }
};
</script>
<style scoped>
.time-inputs-row {
  display: flex;
  gap: 8px;
}
.time-field {
  flex: 1;
  min-width: 80px;
}
.input-label {
  display: block;
  font-size: 0.875rem;
  margin-bottom: 4px;
}
.debug-info {
  font-size: 0.75rem;
  color: #666;
  background-color: #f5f5f5;
  padding: 8px;
  border-radius: 4px;
}
</style>